import { c as _c } from "react/compiler-runtime";
// Шаг «Загрузка»: ход создания товаров, итог (создано / обновлено / с
// ошибкой), ошибки с «Повторить», ссылки на магазин и его товары.
import { Download, ExternalLink, Loader2, Pencil, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import type { CatalogImportItem, CatalogImportJobDetail } from "@osmix/contracts";
import { useCommitImport } from "@/api/hooks/use-catalog-import";
import { Button } from "@osmix/ui/button";
import { IssueList, ProgressBar, StatTile, errorText, formatDateTime, plural, useShopHref } from "./shared";

/** Отчёт об ошибках — CSV: поправить у продавца и загрузить снова. */
const downloadReport = (job: CatalogImportJobDetail, items: CatalogImportItem[]) => {
  const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [["№", "Товар", "Артикул", "Итог", "Причина"].map(cell).join(";")];
  for (const item of items) {
    const reasons = item.issues.filter(i => i.level === "error").map(i => i.message);
    lines.push([String(item.position), item.data.name, item.data.article, item.status === "failed" ? "не загрузилось" : "не загружалось", reasons.join("; ")].map(cell).join(";"));
  }
  const blob = new Blob([`﻿${lines.join("\r\n")}`], {
    type: "text/csv;charset=utf-8"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Ошибки загрузки — ${job.title}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
};
export function CommitStep(t0) {
  const $ = _c(27);
  const {
    job,
    canEdit,
    onOpenItem
  } = t0;
  const commit = useCommitImport(job.id);
  const [error, setError] = useState(null);
  const running = job.status === "committing";
  let committed;
  let t1;
  if ($[0] !== job.items) {
    committed = job.items.filter(_temp);
    t1 = committed.filter(_temp2);
    $[0] = job.items;
    $[1] = committed;
    $[2] = t1;
  } else {
    committed = $[1];
    t1 = $[2];
  }
  const created = t1.length;
  const updated = committed.length - created;
  const failed = job.items.filter(_temp3);
  const pending = job.items.filter(_temp4);
  const skipped = job.items.filter(_temp5);
  const retryIds = [...failed, ...pending].map(_temp6);
  const shopHref = useShopHref(job.shop?.id);
  const editable = canEdit && job.status === "done";
  let t2;
  if ($[3] !== commit) {
    t2 = async itemIds => {
      setError(null);
      ;
      try {
        await commit.mutateAsync({
          itemIds
        });
      } catch (t3) {
        const e = t3;
        setError(errorText(e));
      }
    };
    $[3] = commit;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const retry = t2;
  const t3 = running ? "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u0442\u043E\u0432\u0430\u0440\u044B \u0432 \u043C\u0430\u0433\u0430\u0437\u0438\u043D" : "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0437\u0430\u0432\u0435\u0440\u0448\u0435\u043D\u0430";
  let t4;
  if ($[5] !== t3) {
    t4 = <h2 className="text-lg font-semibold">{t3}</h2>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = running ? "\u0422\u043E\u0432\u0430\u0440\u044B \u0441\u043E\u0437\u0434\u0430\u044E\u0442\u0441\u044F \u0447\u0435\u0440\u0435\u0437 \u0441\u0435\u0440\u0432\u0438\u0441 \u043A\u0430\u0442\u0430\u043B\u043E\u0433\u0430 \u2014 \u0441 \u0442\u0435\u043C\u0438 \u0436\u0435 \u043F\u0440\u043E\u0432\u0435\u0440\u043A\u0430\u043C\u0438 \u0438 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u0435\u0439, \u0447\u0442\u043E \u0443 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0430. \u0421\u043E \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u044B \u043C\u043E\u0436\u043D\u043E \u0443\u0439\u0442\u0438." : `${formatDateTime(job.committedAt)} · ${job.options.publishMode === "draft" ? "\u0442\u043E\u0432\u0430\u0440\u044B \u043B\u0435\u0436\u0430\u0442 \u0447\u0435\u0440\u043D\u043E\u0432\u0438\u043A\u0430\u043C\u0438 \u0443 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0430" : "\u0442\u043E\u0432\u0430\u0440\u044B \u0443\u0448\u043B\u0438 \u043D\u0430 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E"}`;
  let t6;
  if ($[7] !== t5) {
    t6 = <p className="text-sm text-muted-foreground">{t5}</p>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <div className="min-w-0">{t4}{t6}</div>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== job.progress || $[13] !== running) {
    t8 = running && <div className="space-y-1.5" aria-live="polite"><ProgressBar value={job.progress.done} max={Math.max(1, job.progress.total)} /><div className="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground"><span className="min-w-0 truncate">{job.progress.message}</span><span className="tabular-nums">{job.progress.done} из {job.progress.total}</span></div></div>;
    $[12] = job.progress;
    $[13] = running;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = created ? "good" : "default";
  let t10;
  if ($[15] !== created || $[16] !== t9) {
    t10 = <StatTile label={"\u0421\u043E\u0437\u0434\u0430\u043D\u043E"} value={created} tone={t9} />;
    $[15] = created;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== updated) {
    t11 = <StatTile label={"\u041E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u043E"} value={updated} hint={"\u0410\u0440\u0442\u0438\u043A\u0443\u043B \u0443\u0436\u0435 \u0431\u044B\u043B \u0432 \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u0435 \u2014 \u0442\u043E\u0432\u0430\u0440 \u043E\u0431\u043D\u043E\u0432\u043B\u0451\u043D"} />;
    $[18] = updated;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== job.shop || $[21] !== running || $[22] !== shopHref) {
    t12 = !running && job.shop && <div className="flex flex-wrap gap-2">{shopHref && <Button asChild={true} variant="outline"><Link to={shopHref}><ExternalLink className="size-4" />Магазин «{job.shop.name}»</Link></Button>}<Button asChild={true} variant="outline"><Link to={`/products?shopId=${encodeURIComponent(job.shop.id)}`}><ExternalLink className="size-4" />Товары магазина</Link></Button></div>;
    $[20] = job.shop;
    $[21] = running;
    $[22] = shopHref;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== job.target || $[25] !== running) {
    t13 = !running && job.target.kind === "new_shop" && <p className="text-sm text-muted-foreground">{job.target.sendInvite ? "\u041F\u0440\u043E\u0434\u0430\u0432\u0446\u0443 \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u043E \u043F\u0440\u0438\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435 \u0432 \u043A\u0430\u0431\u0438\u043D\u0435\u0442." : "\u041F\u0440\u0438\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0443 \u043D\u0435 \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u044F\u043B\u043E\u0441\u044C \u2014 \u0435\u0433\u043E \u043C\u043E\u0436\u043D\u043E \u0432\u044B\u0441\u043B\u0430\u0442\u044C \u0438\u0437 \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0438 \u0437\u0430\u044F\u0432\u043A\u0438."}</p>;
    $[24] = job.target;
    $[25] = running;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return <div className="flex flex-col gap-4"><section className="flex flex-col gap-4 rounded-lg border p-4 sm:p-6">{t7}{t8}<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{t10}{t11}<StatTile label={"\u0421 \u043E\u0448\u0438\u0431\u043A\u043E\u0439"} value={failed.length} tone={failed.length ? "bad" : "default"} /><StatTile label={"\u041D\u0435 \u0437\u0430\u0433\u0440\u0443\u0436\u0430\u043B\u0438\u0441\u044C"} value={skipped.length} tone="muted" hint={"\u0421 \u043E\u0448\u0438\u0431\u043A\u0430\u043C\u0438 \u0440\u0430\u0437\u0431\u043E\u0440\u0430 \u0438 \u0438\u0441\u043A\u043B\u044E\u0447\u0451\u043D\u043D\u044B\u0435"} /></div>{t12}{t13}</section>{!running && (failed.length > 0 || pending.length > 0) && <section className="flex flex-col gap-3 rounded-lg border p-4 sm:p-6"><div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold">Не загрузилось: {failed.length}{pending.length > 0 && <span className="ml-2 font-normal text-muted-foreground">готово к повтору после правки: {pending.length}</span>}</h3><div className="flex flex-wrap gap-2"><Button variant="outline" size="sm" onClick={() => downloadReport(job, [...failed, ...skipped])}><Download className="size-4" />Отчёт об ошибках</Button>{editable && <Button size="sm" disabled={commit.isPending} onClick={() => void retry(retryIds)}>{commit.isPending ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}Повторить {retryIds.length} {plural(retryIds.length, "\u043F\u043E\u0437\u0438\u0446\u0438\u044E", "\u043F\u043E\u0437\u0438\u0446\u0438\u0438", "\u043F\u043E\u0437\u0438\u0446\u0438\u0439")}</Button>}</div></div>{error && <p role="alert" className="text-sm text-destructive">{error}</p>}<ul className="divide-y">{[...failed, ...pending].map(item => <li key={item.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between"><div className="min-w-0"><p className="text-sm font-medium">{item.data.name}<span className="ml-2 font-normal text-muted-foreground">{item.data.article ? `${item.data.article} · ` : ""}позиция {item.position}</span></p>{item.status === "failed" ? <IssueList issues={item.issues.filter(_temp7)} className="mt-1" /> : <p className="mt-1 text-sm text-muted-foreground">Поправлена — загрузится при повторе.</p>}</div><div className="flex shrink-0 gap-2"><Button variant="outline" size="sm" onClick={() => onOpenItem(item.id, [...failed, ...pending].map(_temp8))}><Pencil className="size-4" />Исправить</Button>{editable && <Button variant="outline" size="sm" disabled={commit.isPending} onClick={() => void retry([item.id])}><RotateCcw className="size-4" />Повторить</Button>}</div></li>)}</ul></section>}</div>;
}
function _temp8(i_6) {
  return i_6.id;
}
function _temp7(i_5) {
  return i_5.level === "error";
}
function _temp6(i_4) {
  return i_4.id;
}
function _temp5(i_3) {
  return i_3.status === "error" || i_3.status === "excluded";
}
function _temp4(i_2) {
  return i_2.status === "ready" || i_2.status === "warning";
}
function _temp3(i_1) {
  return i_1.status === "failed";
}
function _temp2(i_0) {
  return !i_0.existingProductId;
}
function _temp(i) {
  return i.status === "committed";
}